import { TutorSessionFeedbackRating } from '@/queries/constants'

import { MIN_SESSION_HOST_POINTS } from './constants'
import type {
  UserEventPostFeedbackReceived,
  UserEventSessionFeedbackReceived,
  UserEventType,
} from './types'
import { assertUserEventType } from './utils'

const SESSION_RATING_TO_POINTS: Record<
  UserEventSessionFeedbackReceived['payload']['rating'],
  number
> = {
  [TutorSessionFeedbackRating.Helpful]: 1,
  [TutorSessionFeedbackRating.SuperHelpful]: 2,
}

const POST_RATING_TO_POINTS: Record<UserEventPostFeedbackReceived['payload']['rating'], number> = {
  Helpful: 1,
  'Super Helpful': 2,
}

const SCORE_FOR_EVENT: {
  [T in UserEventType['type']]: number | ((userEvent: UserEventType) => number)
} = {
  welcome: 5,
  points_earned: 0,
  achievement_unlocked: 0,

  referred_user: 10,
  referred_by_user: 10,
  tutor_training_completed: 10,
  certification_reviewed: 2,
  session_audited: 4,
  session_safety_audited: 1,
  learner_feedback_given: 1,
  learner_feedback_received: 1,
  audit_reply_submitted: 1,
  verify_peer_reviewer_level: 4,
  resolve_coach_escalation: 4,
  learner_account_deleted: 0,
  post_feedback_given: userEvent => {
    assertUserEventType(userEvent, 'post_feedback_given')
    let score = 1
    if (userEvent.payload.isDetailed) {
      score += 1
    }
    if (userEvent.payload.hasInternalComment === true) {
      score += 1
    }
    return score
  },

  session_feedback_given: userEvent => {
    assertUserEventType(userEvent, 'session_feedback_given')
    let score = 1
    if (userEvent.payload.isDetailed) {
      score += 1
    }
    if (userEvent.payload.hasNps === true) {
      score += 1
    }
    if (userEvent.payload.hasInternalComment === true) {
      score += 1
    }
    return score
  },

  // Note that these get split amongst co-hosts
  session_hosted: userEvent => {
    assertUserEventType(userEvent, 'session_hosted')
    return Math.max(MIN_SESSION_HOST_POINTS, Math.ceil(5 / userEvent.payload.hostsAttendedCount))
  },

  certification_approved: userEvent => {
    assertUserEventType(userEvent, 'certification_approved')
    return userEvent.payload.isCourseChallenge ? 50 : 10
  },

  session_feedback_received: userEvent => {
    assertUserEventType(userEvent, 'session_feedback_received')
    return SESSION_RATING_TO_POINTS[userEvent.payload.rating]
  },

  post_feedback_received: userEvent => {
    assertUserEventType(userEvent, 'post_feedback_received')
    return POST_RATING_TO_POINTS[userEvent.payload.rating]
  },

  session_attended: userEvent => {
    assertUserEventType(userEvent, 'session_attended')
    return userEvent.payload.isRecommendedNewTutorOnboardingSession === true ? 10 : 1
  },
}

export default function getPointsForUserEvent(userEvent: UserEventType): number {
  if (userEvent.type === 'points_earned') {
    return userEvent.payload.points
  }
  const score = SCORE_FOR_EVENT[userEvent.type]
  return typeof score === 'function' ? score(userEvent) : score
}
